import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { LineChart } from '@mui/x-charts/LineChart';
import { CSVLink } from 'react-csv';

const csvHeaders = [
  { label: 'Sale Date', key: 'sale_date' },
  { label: 'Timestamp', key: 'timestamp' },
  { label: 'Invoice No', key: 'invoice_no' },
  { label: 'Cashier', key: 'cashier' },
  { label: 'Item', key: 'item_name' },
  { label: 'Quantity', key: 'qty' },
  { label: 'Cost Price (Rs.)', key: 'cost_price' },
  { label: 'Selling Price (Rs.)', key: 'selling_price' },
  { label: 'Total Cost (Rs.)', key: 'total_cost' },
  { label: 'Total Revenue (Rs.)', key: 'total_revenue' },
  { label: 'Profit (Rs.)', key: 'profit' },
  { label: 'Payment Method', key: 'payment_method' },
  { label: 'Shop', key: 'shop' },
];

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return '0.00';
  }
  return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultRange = () => {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - 13);
  return {
    start: formatInputDate(start),
    end: formatInputDate(today),
  };
};

const ProfitVsRevenueReport = () => {
  const defaultRange = useMemo(() => getDefaultRange(), []);

  const [fromDate, setFromDate] = useState(defaultRange.start);
  const [toDate, setToDate] = useState(defaultRange.end);
  const [search, setSearch] = useState('');
  const [dailyRows, setDailyRows] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    setPage(0);
  }, [fromDate, toDate, search]);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      setError('');
      try {
        const params = { fromDate, toDate };
        const [dailyResponse, transactionResponse] = await Promise.all([
          axios.get('https://serverukautotech.ccsoftwares.com/sales/dailyProfitVsRevenue', { params }),
          axios.get('https://serverukautotech.ccsoftwares.com/sales/profitTransactions', { params }),
        ]);

        const sortedTransactions = (transactionResponse.data || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        setDailyRows(dailyResponse.data || []);
        setTransactions(sortedTransactions);
      } catch (err) {
        console.error('Failed to load profit vs revenue data', err);
        setError('Unable to load profit vs revenue data right now. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [fromDate, toDate]);

  const filteredTransactions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return transactions;
    }
    return transactions.filter((row) => {
      const values = [
        row.inv_no,
        row.item_name,
        row.username,
        row.payment_method,
        row.shop,
        row.sale_date,
      ];
      return values.some((value) => value?.toString().toLowerCase().includes(term));
    });
  }, [transactions, search]);

  const chartRows = useMemo(
    () =>
      [...(dailyRows || [])].sort(
        (a, b) => new Date(a.sale_date) - new Date(b.sale_date)
      ),
    [dailyRows]
  );

  const summary = useMemo(() => {
    return filteredTransactions.reduce(
      (acc, row) => {
        const revenue = Number(row.total_revenue) || 0;
        const cost = Number(row.total_cost) || 0;
        const profit = Number(row.profit) || 0;
        acc.revenue += revenue;
        acc.cost += cost;
        acc.profit += profit;
        acc.qty += Number(row.qty) || 0;
        return acc;
      },
      { revenue: 0, cost: 0, profit: 0, qty: 0 }
    );
  }, [filteredTransactions]);

  const csvData = useMemo(
    () =>
      filteredTransactions.map((row) => ({
        sale_date: row.sale_date,
        timestamp: row.created_at ? new Date(row.created_at).toLocaleString() : '',
        invoice_no: row.inv_no,
        cashier: row.username,
        item_name: row.item_name,
        qty: row.qty,
        cost_price: row.cost_price,
        selling_price: row.selling_price,
        total_cost: row.total_cost,
        total_revenue: row.total_revenue,
        profit: row.profit,
        payment_method: row.payment_method,
        shop: row.shop,
      })),
    [filteredTransactions]
  );

  const paginatedTransactions =
    rowsPerPage === -1
      ? filteredTransactions
      : filteredTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
  };

  const profitMargin =
    summary.revenue > 0 ? ((summary.profit / summary.revenue) * 100).toFixed(2) : '0.00';
  const averageProfit =
    filteredTransactions.length > 0
      ? summary.profit / filteredTransactions.length
      : 0;

  return (
    <Box sx={{ mt: { xs: 2, md: 0 } }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Profit vs Revenue
      </Typography>

      <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Search invoices, items or cashiers"
              placeholder="Start typing to filter transactions"
              fullWidth
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Total Revenue
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(summary.revenue)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Total Profit
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(summary.profit)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Profit Margin
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {profitMargin} %
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#F1F5F9' }}>
            <Typography variant="body2" color="text.secondary">
              Avg. Profit per Transaction
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              Rs. {formatCurrency(averageProfit)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Daily Trend
            </Typography>
            {chartRows.length === 0 ? (
              <Typography color="text.secondary">
                No sales were recorded for the selected period.
              </Typography>
            ) : (
              <LineChart
                height={320}
                series={[
                  {
                    data: chartRows.map((row) => Number(row.revenue) || 0),
                    label: 'Revenue (Rs.)',
                    color: '#0F172A',
                    curve: 'catmullRom',
                  },
                  {
                    data: chartRows.map((row) => Number(row.profit) || 0),
                    label: 'Profit (Rs.)',
                    color: '#16A34A',
                    curve: 'catmullRom',
                  },
                ]}
                xAxis={[
                  {
                    data: chartRows.map((row) => row.sale_date),
                    scaleType: 'point',
                    label: 'Date',
                  },
                ]}
                slotProps={{
                  legend: { direction: 'row', position: { vertical: 'top', horizontal: 'center' } },
                }}
              />
            )}
          </Paper>

          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Transactions</Typography>
              <CSVLink
                data={csvData}
                headers={csvHeaders}
                filename={`profit-transactions_${fromDate}_${toDate}.csv`}
                style={{ textDecoration: 'none' }}
              >
                <Button
                  startIcon={<DownloadIcon />}
                  variant="contained"
                  disabled={!filteredTransactions.length}
                  sx={{ alignSelf: { xs: 'flex-start', md: 'auto' } }}
                >
                  Download CSV
                </Button>
              </CSVLink>
            </Stack>

            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: '#000000' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Invoice</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Cashier</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Item</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Qty</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Cost (Rs.)</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Selling (Rs.)</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Revenue (Rs.)</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Profit (Rs.)</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Payment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography color="text.secondary">
                          No transactions match your filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedTransactions.map((row) => (
                      <TableRow key={`${row.inv_no}-${row.item_name}-${row.created_at}`}>
                        <TableCell>
                          {row.created_at ? new Date(row.created_at).toLocaleString() : row.sale_date}
                        </TableCell>
                        <TableCell>{row.inv_no}</TableCell>
                        <TableCell>{row.username}</TableCell>
                        <TableCell>{row.item_name}</TableCell>
                        <TableCell>{row.qty}</TableCell>
                        <TableCell>{formatCurrency(row.cost_price)}</TableCell>
                        <TableCell>{formatCurrency(row.selling_price)}</TableCell>
                        <TableCell>{formatCurrency(row.total_revenue)}</TableCell>
                        <TableCell>{formatCurrency(row.profit)}</TableCell>
                        <TableCell>{row.payment_method || 'N/A'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredTransactions.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ProfitVsRevenueReport;
